// src/components/block/liveStreamManagement/RecordingStorageModal.tsx
// v2.2 — Colonnes durée/taille élargies, padding réduit, icônes actions 32×32
// v2.3 (2026-10-23) — présentation : résumé en cartes (prises, taille, disque du
//   compte avec jauge, reste à enregistrer), bouton Actualiser, badge EN COURS,
//   date lisible, actions réordonnées (Publier en tête, Supprimer à part) et
//   confirmation en vrais boutons, état vide expliqué. Logique inchangée.

import React, {
  useEffect, useCallback, useState, useMemo, useRef,
} from 'react';
import ReactDOM from 'react-dom';
import {
  X, HardDrive, Download, Trash2, Upload,
  RefreshCw, VideoOff, AlertTriangle, ChevronLeft,
  ChevronUp, ChevronDown, ChevronsUpDown, Pencil, Check,
  Film, Database, Clock, ChevronRight,
} from 'lucide-react';

import {
  RecordingSession,
  computeDuration,
  durationInSeconds,
  formatFileSize,
  resolveFileUrl,
  getRecordingSessions,
  deleteRecordingSession,
  renameRecordingSession,
  sanitizeRecordingFilenameInput,
  findForbiddenRecordingFilenameChars,
  empiricalBytesPerSecond,
  configuredBytesPerSecond,
  estimateRemainingRecordingSeconds,
  formatRemainingDuration,
} from '../../../backendServices/liveStreamManagment/api_recording';

import VideoSheet2 from '../mediaManagment/VideoSheet2';
import { estSansLimite } from '../../../dataTypes/adminManagment/quota';

// ─── Props ────────────────────────────────────────────────────────────────────

interface RecordingStorageModalProps {
  channelUuid : string;
  channelName : string;
  managerUrl  : string;
  apiKey      : string;
  // ── Estimation du quota disque restant (footer) ──────────────────────────
  adminId                    : number;
  quotaDiskBytes             : number;
  quotaUsedBytes             : number;
  configuredVideoBitrateKbps : number;
  configuredAudioBitrateKbps : number;
  onClose     : () => void;
}

// ─── Types tri ────────────────────────────────────────────────────────────────

/** Le programme d'une prise multi-angle, sous lequel se rangent ses angles. */
const isTakeProgram = (s: RecordingSession) => !!s.takeId && !!s.takeProgram;

type SortKey = 'filename' | 'startedAt' | 'duration' | 'sizeBytes';
type SortDir = 'asc' | 'desc';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  const d   = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** « jeu. 23 oct. 2026 » — la date d'une prise, lisible d'un coup d'œil. */
function formatDay(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

/** « 18:19 » */
function formatHour(iso: string): string {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/** Carte du résumé en tête de liste ; `bar` = taux d'occupation (0–100) à dessiner. */
const SummaryCard: React.FC<{
  icon: React.ReactNode; label: string; value: string; sub?: string; subColor?: string;
  bar?: number | null; title?: string;
}> = ({ icon, label, value, sub, subColor, bar, title }) => {
  const barColor = bar == null ? '' : bar >= 90 ? '#ef4444' : bar >= 75 ? '#f59e0b' : '#22c55e';
  return (
    <div title={title} style={{
      flex: '1 1 0', minWidth: 0, padding: '12px 14px', borderRadius: 12,
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
      display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        <span style={{ color: '#60a5fa', display: 'flex' }}>{icon}</span>{label}
      </span>
      <span style={{ fontSize: 19, fontWeight: 700, color: '#f8fafc', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
      {bar != null && (
        <span style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
          <span style={{ display: 'block', height: '100%', width: `${bar}%`, background: barColor, borderRadius: 3, transition: 'width 0.3s' }} />
        </span>
      )}
      {sub && <span style={{ fontSize: 12, color: subColor ?? '#94a3b8' }}>{sub}</span>}
    </div>
  );
};

/** Durée en secondes pour le tri */
function durationSec(session: RecordingSession): number {
  if (!session.stoppedAt) return Infinity; // "En cours" tout en bas par défaut
  return durationInSeconds(session) ?? -1; // inconnue → tout en haut, visible pour investigation
}

// ─── Composant ────────────────────────────────────────────────────────────────

const RecordingStorageModal: React.FC<RecordingStorageModalProps> = ({
  channelUuid, channelName, managerUrl, apiKey,
  adminId, quotaDiskBytes, quotaUsedBytes, configuredVideoBitrateKbps, configuredAudioBitrateKbps,
  onClose,
}) => {
  const [sessions,        setSessions]        = useState<RecordingSession[]>([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState<string | null>(null);
  const [deletingId,      setDeletingId]      = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [previewSession,  setPreviewSession]  = useState<RecordingSession | null>(null);
  const [publishingSession, setPublishingSession] = useState<RecordingSession | null>(null);

  // ── Renommage ────────────────────────────────────────────────────────────────
  const [renamingId,   setRenamingId]   = useState<number | null>(null);
  const [renameValue,  setRenameValue]  = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  // Bulle « caractère interdit » : affichée à la frappe, effacée toute seule.
  // Un ref pour le timer plutôt qu'un state — il ne doit provoquer aucun rendu.
  // Position en `fixed`, calculée depuis le champ et portée hors du tableau :
  // le `<td>` coupe tout ce qui dépasse sa cellule (overflow: hidden, pour la
  // troncature du titre), donc une bulle attachée dans la cellule se retrouvait
  // à moitié rognée par la ligne suivante.
  const [renameForbiddenMsg, setRenameForbiddenMsg] = useState<string | null>(null);
  const [renameBubblePos,    setRenameBubblePos]    = useState<{ top: number; left: number; above: boolean } | null>(null);
  const renameForbiddenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Tri ─────────────────────────────────────────────────────────────────────
  const [sortKey, setSortKey] = useState<SortKey>('startedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = useCallback((key: SortKey) => {
    setSortKey(prev => {
      if (prev === key) {
        setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        return key;
      }
      setSortDir('asc');
      return key;
    });
  }, []);

  // Nombre de fichiers par prise multi-angle : le programme, plus un par angle.
  const takeSizes = useMemo(() => {
    const sizes = new Map<string, number>();
    sessions.forEach(s => { if (s.takeId) sizes.set(s.takeId, (sizes.get(s.takeId) ?? 0) + 1); });
    return sizes;
  }, [sessions]);

  const [expandedTakes, setExpandedTakes] = useState<Set<string>>(() => new Set());

  const sortedSessions = useMemo(() => {
    return [...sessions].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'filename':
          cmp = a.filename.localeCompare(b.filename);
          break;
        case 'startedAt':
          cmp = new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime();
          break;
        case 'duration':
          cmp = durationSec(a) - durationSec(b);
          break;
        case 'sizeBytes':
          cmp = (a.sizeBytes ?? -1) - (b.sizeBytes ?? -1);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [sessions, sortKey, sortDir]);

  // Prises dépliées : toutes pliées à l'ouverture du panneau, seul le programme se voit.
  const toggleTake = useCallback((takeId: string) => {
    setExpandedTakes(prev => {
      const next = new Set(prev);
      if (!next.delete(takeId)) next.add(takeId);
      return next;
    });
  }, []);

  // Les angles d'une prise se rangent sous son programme, quel que soit le tri. Sans programme
  // (supprimé ou renommé), ils restent à leur place dans la liste.
  const { displayedSessions, nestedAngleIds } = useMemo(() => {
    const programs = new Set(sessions.filter(isTakeProgram).map(s => s.takeId));
    const isNested = (s: RecordingSession) => !!s.takeId && programs.has(s.takeId) && !isTakeProgram(s);
    const nested   = sortedSessions.filter(isNested);
    return {
      displayedSessions: sortedSessions.filter(s => !isNested(s)).flatMap(s =>
        isTakeProgram(s) && expandedTakes.has(s.takeId as string) ? [s, ...nested.filter(angle => angle.takeId === s.takeId)] : [s]),
      nestedAngleIds: new Set(nested.map(s => s.id)),
    };
  }, [sessions, sortedSessions, expandedTakes]);

  // ── Estimation du quota disque restant ─────────────────────────────────────
  const remainingRecordingSec = useMemo(() => {
    const bytesPerSecond = empiricalBytesPerSecond(sessions, adminId)
      ?? configuredBytesPerSecond(configuredVideoBitrateKbps, configuredAudioBitrateKbps);
    return estimateRemainingRecordingSeconds(quotaDiskBytes, quotaUsedBytes, bytesPerSecond);
  }, [sessions, adminId, quotaDiskBytes, quotaUsedBytes, configuredVideoBitrateKbps, configuredAudioBitrateKbps]);

  // ── Résumé en tête de liste (2026-10-23) ───────────────────────────────────
  const summary = useMemo(() => {
    const channelBytes = sessions.reduce((sum, s) => sum + (s.sizeBytes ?? 0), 0);
    const liveCount    = sessions.filter(s => !s.stoppedAt).length;
    // −1 = sans limite ; 0 = aucun espace acheté (cf. dataTypes/adminManagment/quota.ts).
    const unlimited    = estSansLimite(quotaDiskBytes);
    const usedPct      = unlimited ? null
                       : quotaDiskBytes > 0 ? Math.min(100, Math.max(0, (quotaUsedBytes / quotaDiskBytes) * 100))
                       : 100;
    return { channelBytes, liveCount, unlimited, usedPct };
  }, [sessions, quotaDiskBytes, quotaUsedBytes]);

  // ── Chargement ───────────────────────────────────────────────────────────────
  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRecordingSessions(channelUuid);
      setSessions(data);
    } catch (err: any) {
      setError(err.message ?? 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [channelUuid]);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  // ── Actions ───────────────────────────────────────────────────────────────────
  const handleDownload = useCallback((session: RecordingSession) => {
	  
	  
	  
    const url = resolveFileUrl(session, managerUrl, apiKey, channelUuid, 'attachment');

	console.log ("  handleDownload >>", session, url)  

    const a   = document.createElement('a');
    a.href    = url;
    a.download = `${session.filename}.${session.format}`;
    a.target  = '_blank';
    a.rel     = 'noopener noreferrer';
    a.click();
  }, [managerUrl, apiKey, channelUuid]);


  const handleDeleteConfirm = useCallback(async (sessionId: number) => {
    setDeletingId(sessionId);
    setConfirmDeleteId(null);
    try {
      await deleteRecordingSession(channelUuid, sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (previewSession?.id === sessionId) setPreviewSession(null);
    } catch (err: any) {
      setError(`Erreur suppression : ${err.message}`);
    } finally {
      setDeletingId(null);
    }
  }, [channelUuid, previewSession]);

  const handleRenameStart = useCallback((session: RecordingSession) => {
    setRenamingId(session.id);
    setRenameValue(session.filename);
    setRenameForbiddenMsg(null);
    setError(null);
  }, []);

  const handleRenameCancel = useCallback(() => {
    setRenamingId(null);
    setRenameForbiddenMsg(null);
    if (renameForbiddenTimeoutRef.current) {
      clearTimeout(renameForbiddenTimeoutRef.current);
      renameForbiddenTimeoutRef.current = null;
    }
  }, []);

  // Filtre au fil de la frappe et affiche une bulle le temps que le caractère
  // refusé reste visible à l'écran — sinon rien ne distingue une frappe
  // ignorée d'un clavier qui ne répond plus.
  const handleRenameChange = useCallback((raw: string, inputEl: HTMLInputElement) => {
    const forbidden = findForbiddenRecordingFilenameChars(raw);

    if (renameForbiddenTimeoutRef.current) {
      clearTimeout(renameForbiddenTimeoutRef.current);
      renameForbiddenTimeoutRef.current = null;
    }

    if (forbidden.length > 0) {
      const rect = inputEl.getBoundingClientRect();

      // Abscisse du curseur : c'est là que le caractère refusé a été frappé, et la bulle
      // se pose au-dessus de CET endroit plutôt qu'au début du champ. Le texte avant le
      // curseur, une fois nettoyé, est ce qui reste réellement à l'écran ; sa largeur se
      // mesure avec la police du champ (canvas), moins le défilement horizontal du champ.
      const cs     = getComputedStyle(inputEl);
      const ctx    = document.createElement('canvas').getContext('2d');
      let caretX   = rect.left + parseFloat(cs.paddingLeft || '0') + parseFloat(cs.borderLeftWidth || '0');
      if (ctx) {
        ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
        const before = sanitizeRecordingFilenameInput(raw.slice(0, inputEl.selectionStart ?? raw.length));
        caretX += ctx.measureText(before).width - inputEl.scrollLeft;
      }
      caretX = Math.min(Math.max(caretX, rect.left), rect.right);

      // Bord gauche de la bulle un peu en retrait du curseur, sans sortir de l'écran ;
      // largeur d'une bulle courante estimée à 240 px.
      const left = Math.max(8, Math.min(caretX - 12, window.innerWidth - 240));

      // Au-dessus du champ ; en dessous seulement s'il n'y a pas la place (champ collé
      // au haut de l'écran), la bulle serait sinon rognée.
      const above = rect.top >= 40;
      setRenameBubblePos(above
        ? { top: rect.top - 5,    left, above: true  }
        : { top: rect.bottom + 5, left, above: false });
      setRenameForbiddenMsg(
        `Non autorisé : ${forbidden.map(c => c === ' ' ? '(espace)' : c).join(' ')}`
      );
      renameForbiddenTimeoutRef.current = setTimeout(() => setRenameForbiddenMsg(null), 2200);
    } else {
      setRenameForbiddenMsg(null);
    }

    setRenameValue(sanitizeRecordingFilenameInput(raw));
  }, []);

  const handleRenameConfirm = useCallback(async (session: RecordingSession) => {
    const trimmed = renameValue.trim();

    if (trimmed === '') {
      setError('Le nom ne peut pas être vide');
      return;
    }
    if (trimmed === session.filename) {
      setRenamingId(null);
      return;
    }

    setRenameSaving(true);
    setError(null);
    try {
      await renameRecordingSession(channelUuid, session.id, trimmed);
      setRenamingId(null);
      await loadSessions();
    } catch (err: any) {
      setError(err.message ?? 'Erreur renommage');
    } finally {
      setRenameSaving(false);
    }
  }, [renameValue, channelUuid, loadSessions]);

  // ── Keyboard ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (renamingId !== null) { handleRenameCancel(); return; }
        if (previewSession) { setPreviewSession(null); return; }
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, previewSession, renamingId, handleRenameCancel]);

  // Le timer de la bulle ne doit pas survivre au démontage de la fenêtre.
  useEffect(() => () => {
    if (renameForbiddenTimeoutRef.current) clearTimeout(renameForbiddenTimeoutRef.current);
  }, []);

  const handlePublish = useCallback((session: RecordingSession) => {
    // Les enregistrements antérieurs à la bascule vers le workingSpace
    // (2026-08/09) n'ont pas de storagePath : le fichier vit encore sur
    // zircon, servi seulement en HTTP par mediamtx-manager — aucun chemin
    // local à donner au transcodage. Republication indisponible pour eux
    // dans ce premier jet (voir VideoFromRecordingAssembler côté admin-api).
    if (!session.storagePath) {
      setError("Cet enregistrement n'a pas encore été rangé — republication indisponible pour l'instant.");
      return;
    }
    setPublishingSession(session);
  }, []);

  const previewUrl = previewSession
    ? resolveFileUrl(previewSession, managerUrl, apiKey, channelUuid, 'inline')
    : null;

  // ── Icône de tri ──────────────────────────────────────────────────────────────
  const SortIcon: React.FC<{ col: SortKey }> = ({ col }) => {
    if (sortKey !== col) return <ChevronsUpDown style={{ width: 12, height: 12, opacity: 0.35 }} />;
    return sortDir === 'asc'
      ? <ChevronUp   style={{ width: 12, height: 12, color: '#60a5fa' }} />
      : <ChevronDown style={{ width: 12, height: 12, color: '#60a5fa' }} />;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return <>
  {ReactDOM.createPortal(
    <div style={S.overlay}>
      <div onClick={e => e.stopPropagation()} style={S.modal}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={S.header}>
          {previewSession ? (
            <button onClick={() => setPreviewSession(null)} style={S.backBtn} title="Retour à la liste">
              <ChevronLeft style={{ width: 15, height: 15 }} />
            </button>
          ) : (
            <span style={S.headerIcon}><HardDrive style={{ width: 17, height: 17, color: '#60a5fa' }} /></span>
          )}
          <span style={S.headerTitle}>
            {previewSession
              ? <><span style={{ color: '#94a3b8' }}>Visionnage — </span>{previewSession.filename}</>
              : <>Stockage des enregistrements<span style={{ color: '#94a3b8', fontWeight: 500 }}> · {channelName}</span></>
            }
          </span>
          {!previewSession && (
            <button onClick={loadSessions} disabled={loading} style={{ ...S.headerBtn, opacity: loading ? 0.5 : 1 }} title="Relire la liste des enregistrements">
              <RefreshCw style={{ width: 14, height: 14, animation: loading ? 'rs-spin 1s linear infinite' : undefined }} /> Actualiser
            </button>
          )}
          <button onClick={onClose} style={S.closeIconBtn} title="Fermer (Échap)">
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* ── Corps ──────────────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

          {/* Vue lecteur */}
          {previewSession && previewUrl && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <video
                src      = {previewUrl}
                controls
                autoPlay
                style    = {{ width: '100%', flex: 1, background: '#000', maxHeight: 400 }}
              />
              <div style={{ padding: '10px 20px', borderTop: '1px solid #1e293b' }}>
                <span style={{ fontSize: 14, color: '#ffffff' }}>
                  {formatDateTime(previewSession.startedAt)}
                  {' · '}{computeDuration(previewSession)}
                  {' · '}{formatFileSize(previewSession.sizeBytes)}
                </span>
              </div>
            </div>
          )}

          {/* Résumé : la chaîne, le compte, ce qui reste à enregistrer */}
          {!previewSession && (
            <div style={S.summary}>
              <SummaryCard icon={<Film style={{ width: 16, height: 16 }} />} label="Enregistrements"
                value={loading ? '…' : String(sessions.length)}
                sub={!loading && summary.liveCount > 0 ? `dont ${summary.liveCount} en cours` : 'de cette chaîne'}
                subColor={summary.liveCount > 0 ? '#facc15' : undefined} />
              <SummaryCard icon={<Database style={{ width: 16, height: 16 }} />} label="Taille de la chaîne"
                value={loading ? '…' : formatFileSize(summary.channelBytes || null)}
                sub="somme des prises listées" />
              <SummaryCard icon={<HardDrive style={{ width: 16, height: 16 }} />} label="Disque du compte"
                value={summary.unlimited ? '∞' : `${formatFileSize(quotaUsedBytes || null)} / ${formatFileSize(quotaDiskBytes || null)}`}
                sub={summary.unlimited ? 'sans limite' : `${Math.round(summary.usedPct ?? 0)} % utilisés`}
                bar={summary.usedPct} />
              <SummaryCard icon={<Clock style={{ width: 16, height: 16 }} />} label="Reste à enregistrer"
                value={loading ? '…' : formatRemainingDuration(remainingRecordingSec)}
                sub="au débit de vos prises"
                title="Estimation basée sur le débit moyen des enregistrements précédents (ou sur le bitrate configuré à défaut d'historique suffisant)"
                subColor={remainingRecordingSec !== null && remainingRecordingSec <= 0 ? '#f87171' : undefined} />
            </div>
          )}

          {/* Erreur */}
          {!previewSession && error && (
            <div style={S.errorBanner}>
              <AlertTriangle style={{ width: 15, height: 15, color: '#f87171', flexShrink: 0 }} />
              <span style={{ fontSize: 14, color: '#fecaca', flex: 1 }}>{error}</span>
              <button onClick={() => setError(null)} style={{ ...S.iconBtn, color: '#fca5a5' }} title="Masquer">
                <X style={{ width: 14, height: 14 }} />
              </button>
            </div>
          )}

          {/* Chargement */}
          {!previewSession && loading && (
            <div style={S.centered}>
              <RefreshCw style={{ width: 16, height: 16, animation: 'rs-spin 1s linear infinite', color: '#60a5fa' }} />
              <span style={{ fontSize: 14, color: '#cbd5e1' }}>Chargement des enregistrements…</span>
            </div>
          )}

          {/* Vide */}
          {!previewSession && !loading && sessions.length === 0 && !error && (
            <div style={{ ...S.centered, flexDirection: 'column', gap: 12 }}>
              <span style={S.emptyIcon}><VideoOff style={{ width: 28, height: 28, color: '#94a3b8' }} /></span>
              <span style={{ fontSize: 16, fontWeight: 600, color: '#f1f5f9' }}>Aucun enregistrement pour cette chaîne</span>
              <span style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', maxWidth: 420, lineHeight: 1.6 }}>
                Les prises lancées avec le bouton <strong style={{ color: '#fca5a5' }}>REC</strong> de la régie apparaissent ici, prêtes à être téléchargées, renommées ou publiées.
              </span>
            </div>
          )}

          {/* Tableau */}
          {!previewSession && !loading && sessions.length > 0 && (
            <table style={S.table}>
              {/* Titre, Date, Durée, Taille, Actions. Durée et Taille sont des
                  nombres courts : ce qu'on leur retire va au Titre, seul à être
                  tronqué. */}
              <colgroup>
                <col style={{ width: '38%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '25%' }} />
              </colgroup>

              {/* En-têtes */}
              <thead>
                <tr>
                  {(
                    [
                      { key: 'filename',  label: 'Titre'    },
                      { key: 'startedAt', label: 'Date'     },
                      { key: 'duration',  label: 'Durée'    },
                      { key: 'sizeBytes', label: 'Taille'   },
                    ] as { key: SortKey; label: string }[]
                  ).map(col => (
                    <th
                      key     = {col.key}
                      onClick = {() => handleSort(col.key)}
                      style   = {{
                        ...S.th,
                        color: sortKey === col.key ? '#ffffff' : '#94a3b8',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {col.label}
                        <SortIcon col={col.key} />
                      </span>
                    </th>
                  ))}
                  <th style={{ ...S.th, cursor: 'default', textAlign: 'right' }}>
                    <span style={{ color: '#94a3b8' }}>Actions</span>
                  </th>
                </tr>
              </thead>

              {/* Lignes */}
              <tbody>
                {displayedSessions.map(session => {
                  const isDeleting = deletingId      === session.id;
                  const isConfirm  = confirmDeleteId === session.id;
                  const inProgress = !session.stoppedAt;
                  const isRenaming = renamingId      === session.id;
                  const takeAngles = session.takeId ? (takeSizes.get(session.takeId) ?? 1) - 1 : 0;
                  const isNestedAngle = nestedAngleIds.has(session.id);
                  const canRename  = !inProgress && !!session.storagePath;

                  return (
                    <tr key={session.id} className="rs-tr" style={{ ...S.tr, ...(isConfirm ? { background: 'rgba(239,68,68,0.07)' } : {}) }}>

                      {/* Titre — tronqué à la largeur de la colonne, avec le nom
                          entier en bulle. `title` natif plutôt qu'une infobulle
                          maison : elle survit au défilement, ne coûte aucun rendu,
                          et se pose déjà sur le point rouge juste à côté. */}
                      <td style={isNestedAngle ? { ...S.td, paddingLeft: 44 } : S.td}>
                        {isRenaming ? (
                          <input
                            autoFocus
                            value     = {renameValue}
                            // Filtré au fil de la frappe plutôt que rejeté après coup :
                            // le champ n'accepte tout simplement pas ce qui casserait un
                            // système de fichiers ou une URL — accents compris, en
                            // revanche, ils passent (cf. RECORDING_FILENAME_PATTERN).
                            onChange  = {e => handleRenameChange(e.target.value, e.target)}
                            onKeyDown = {e => {
                              if (e.key === 'Enter')  { e.preventDefault(); handleRenameConfirm(session); }
                              if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); handleRenameCancel(); }
                            }}
                            disabled  = {renameSaving}
                            style     = {S.renameInput}
                          />
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            {isNestedAngle && <span aria-hidden="true" style={{ color: '#8f75e8', flexShrink: 0 }}>└</span>}
                            {takeAngles > 0 && isTakeProgram(session) && (
                              <button
                                type          = "button"
                                aria-expanded = {expandedTakes.has(session.takeId as string)}
                                title         = {expandedTakes.has(session.takeId as string) ? 'Plier les angles' : 'Déplier les angles'}
                                onClick       = {() => toggleTake(session.takeId as string)}
                                style         = {S.takeToggle}
                              >
                                <ChevronRight style={{ width: 16, height: 16, transition: 'transform 0.15s', transform: expandedTakes.has(session.takeId as string) ? 'rotate(90deg)' : 'none' }} />
                              </button>
                            )}
                            <span style={{ ...S.fileIcon, ...(inProgress ? S.fileIconLive : {}) }}>
                              <Film style={{ width: 15, height: 15 }} />
                            </span>
                            <span
                              title={`${session.filename}.${session.format}`}
                              style={{
                                fontSize    : 14,
                                fontWeight  : 600,
                                color       : '#f1f5f9',
                                overflow    : 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace  : 'nowrap',
                                // `minWidth: 0` sur l'enfant d'un flex : sans lui, la
                                // largeur minimale d'un texte insécable l'emporte sur
                                // `overflow: hidden` et rien n'est jamais tronqué.
                                minWidth    : 0,
                                flex        : 1,
                              }}
                            >
                              {session.filename}<span style={{ color: '#64748b', fontWeight: 500 }}>.{session.format}</span>
                            </span>
                            {inProgress && (
                              <span style={S.liveBadge} title="Enregistrement en cours"><span style={S.liveDot} /> EN COURS</span>
                            )}
                            {takeAngles > 0 && (isTakeProgram(session)
                              ? <span style={S.multiviewBadge} title="Programme d'une prise multiview : à publier pour obtenir une vidéo multiview">MULTIVIEW · {takeAngles} ANGLE{takeAngles > 1 ? 'S' : ''}</span>
                              : <span style={S.angleBadge} title="Angle d'une prise multiview">ANGLE</span>)}
                          </div>
                        )}
                      </td>

                      {/* Date */}
                      <td style={{ ...S.td, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }} title={formatDateTime(session.startedAt)}>
                        <span style={{ fontSize: 14, color: '#e2e8f0' }}>{formatDay(session.startedAt)}</span>
                        <span style={{ fontSize: 13, color: '#94a3b8', marginLeft: 8 }}>{formatHour(session.startedAt)}</span>
                      </td>

                      {/* Durée — whiteSpace nowrap pour "En cours" sur une ligne */}
                      <td style={{ ...S.td, fontSize: 14, fontWeight: 600, color: inProgress ? '#facc15' : '#e2e8f0', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {computeDuration(session)}
                      </td>

                      {/* Taille */}
                      <td style={{ ...S.td, fontSize: 14, color: '#e2e8f0', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {formatFileSize(session.sizeBytes)}
                      </td>

                      {/* Actions */}
                      <td style={{ ...S.td, textAlign: 'right' }}>
                        {isRenaming ? (

                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <IconBtn
                              icon     = {renameSaving
                                ? <RefreshCw style={{ width: 20, height: 20, animation: 'rs-spin 1s linear infinite' }} />
                                : <Check style={{ width: 20, height: 20 }} />}
                              color    = "#4ade80"
                              title    = "Valider"
                              onClick  = {() => handleRenameConfirm(session)}
                              disabled = {renameSaving}
                            />
                            <IconBtn
                              icon     = {<X style={{ width: 20, height: 20 }} />}
                              color    = "#ffffff"
                              title    = "Annuler"
                              onClick  = {handleRenameCancel}
                              disabled = {renameSaving}
                            />
                          </span>

                        ) : isConfirm ? (

						  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
							<span style={{ fontSize: 13, color: '#fca5a5', fontWeight: 600, marginRight: 2 }}>Supprimer ?</span>
							<button type="button" style={S.confirmYes} onClick={() => handleDeleteConfirm(session.id)}>Oui</button>
							<button type="button" style={S.confirmNo}  onClick={() => setConfirmDeleteId(null)}>Non</button>
						  </span>

                        ) : (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <IconBtn
                              icon     = {<Upload style={{ width: 18, height: 18 }} />}
                              color    = "#60a5fa"
                              title    = {inProgress ? 'Enregistrement en cours' : 'Publier en tant que Vidéo'}
                              onClick  = {() => handlePublish(session)}
                              disabled = {isDeleting || inProgress}
                            />
                            <IconBtn
                              icon     = {<Download style={{ width: 18, height: 18 }} />}
                              color    = "#e2e8f0"
                              title    = "Télécharger"
                              onClick  = {() => handleDownload(session)}
                              disabled = {isDeleting}
                            />
                            <IconBtn
                              icon     = {<Pencil style={{ width: 18, height: 18 }} />}
                              color    = "#e2e8f0"
                              title    = {inProgress ? 'Enregistrement en cours' : canRename ? 'Renommer' : "Pas encore rangé — renommage indisponible"}
                              onClick  = {() => handleRenameStart(session)}
                              disabled = {isDeleting || !canRename}
                            />
							{/*
                            <IconBtn
                              icon     = {<Play style={{ width: 20, height: 20 }} />}
                              color    = "#ffffff"
                              title    = {inProgress ? 'Enregistrement en cours' : 'Visionner'}
                              onClick  = {() => handlePreview(session)}
                              disabled = {isDeleting || inProgress}
                            />
							*/}
                            <span style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', margin: '0 2px' }} />
                            <IconBtn
                              icon     = {isDeleting
                                ? <RefreshCw style={{ width: 18, height: 18, animation: 'rs-spin 1s linear infinite' }} />
                                : <Trash2 style={{ width: 18, height: 18 }} />}
                              color    = "#f87171"
                              title    = "Supprimer"
                              onClick  = {() => setConfirmDeleteId(session.id)}
                              disabled = {isDeleting}
                            />
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div style={S.footer}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>
            Cliquez un en-tête de colonne pour trier · <kbd style={S.kbd}>Échap</kbd> pour fermer
          </span>
          <button onClick={onClose} style={S.closeBtn}>Fermer</button>
        </div>

      </div>

      <style>{`
        @keyframes rs-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .rs-th-btn:hover {
          background: rgba(255,255,255,0.04) !important;
        }
        .rs-icon-btn:hover:not(:disabled) {
          opacity: 1 !important;
          background: var(--rs-btn-bg-hover) !important;
        }
        .rs-tr:hover td {
          background: rgba(255,255,255,0.035);
        }
        @keyframes rs-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
        @media (prefers-reduced-motion: reduce) { .rs-tr span { animation: none !important; } }
      `}</style>
    </div>,
    document.body
  )}
  {publishingSession && (
    <RecordingPublishModal
      session     = {publishingSession}
      channelUuid = {channelUuid}
      managerUrl  = {managerUrl}
      apiKey      = {apiKey}
      // Programme d'une prise multi-angle : les autres fichiers de la prise, déjà rangés, sont ses angles.
      angleCount  = {isTakeProgram(publishingSession)
        ? sessions.filter(s => s.takeId === publishingSession.takeId && s.id !== publishingSession.id && !!s.storagePath).length
        : 0}
      onClose     = {() => setPublishingSession(null)}
    />
  )}
  {/* Bulle « caractère interdit » — portée hors du tableau (voir le commentaire
      sur renameForbiddenMsg) : `position: fixed`, coordonnées viewport prises sur
      le champ au moment de la frappe. */}
  {renameForbiddenMsg && renameBubblePos && ReactDOM.createPortal(
    <div style={{ ...S.renameForbiddenBubble, top: renameBubblePos.top, left: renameBubblePos.left, ...(renameBubblePos.above ? { transform: 'translateY(-100%)' } : {}) }}>
      {renameForbiddenMsg}
    </div>,
    document.body
  )}
  </>;
};

// ─── RecordingPublishModal ────────────────────────────────────────────────────

/**
 * Fenêtre « Publier » d'un enregistrement : fiche vidéo pré-remplie depuis la
 * session. Sortie de RecordingStorageModal le 22/10/2026 pour servir aussi à la
 * régie, qui la propose à l'arrêt d'un live enregistré automatiquement.
 * L'appelant doit avoir vérifié `session.storagePath` (cf. handlePublish).
 */
export const RecordingPublishModal: React.FC<{
  session    : RecordingSession;
  channelUuid: string;
  managerUrl : string;
  apiKey     : string;
  angleCount?: number;
  onClose    : () => void;
}> = ({ session, channelUuid, managerUrl, apiKey, angleCount, onClose }) => {
  // Élément de l'en-tête de la fenêtre de publication où VideoSheet2 projette
  // sa barre Annuler/Créer (même mécanisme que MediaSheet). State et non ref :
  // la fiche doit se re-rendre quand l'élément est monté.
  const [publishActionsSlot, setPublishActionsSlot] = useState<HTMLDivElement | null>(null);
  const publishPreviewUrl = resolveFileUrl(session, managerUrl, apiKey, channelUuid, 'inline');

  return ReactDOM.createPortal(
    <div style={S.overlay}>
      {/* Élargie le 04/09/2026 : à 900px la fiche vidéo (min-width 1000px,
          layout 3 colonnes) débordait et forçait un défilement horizontal.
          1200px suffisait tout juste à tenir les 3 colonnes sans déborder,
          mais chaque colonne restait trop étroite pour son propre contenu
          (libellés sur deux lignes, options de select coupées) — 1600px
          laisse à chaque colonne (~1fr/1.3fr/1.3fr) une largeur réelle.
          Agrandie encore le 20/09/2026 (1800px / 94vh) : la fiche montait
          jusqu'au bas de la fenêtre et obligeait à défiler pour atteindre les
          boutons — ils sont maintenant dans l'en-tête, comme à la création
          d'une vidéo ordinaire. */}
      <div onClick={e => e.stopPropagation()} style={{ ...S.modal, maxWidth: 1800, maxHeight: '94vh' }}>
        <div style={S.header}>
          <Upload style={{ width: 16, height: 16, color: '#60a5fa', flexShrink: 0 }} />
          <span style={S.headerTitle}>
            <span style={{ color: '#94a3b8' }}>Publier — </span>
            {session.filename}.{session.format}
          </span>
          <div ref={setPublishActionsSlot} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>
          <VideoSheet2
            prefillFromRecording={{
              channelUuid    : channelUuid,
              sessionId      : session.id,
              filename       : session.filename,
              format         : session.format,
              durationSeconds: session.durationSeconds,
              angleCount     : angleCount,
              previewUrl     : publishPreviewUrl ?? undefined,
            }}
            // Appelé par VideoSheet2 après une création réussie ET après "Annuler"
            // (id vaut alors 0, même convention que MediaSheet) : dans les deux cas,
            // il n'y a plus rien à faire ici, la fenêtre se ferme.
            onCreate={onClose}
            actionsSlot={publishActionsSlot}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── IconBtn ──────────────────────────────────────────────────────────────────

const IconBtn: React.FC<{
  icon     : React.ReactNode;
  color    : string;
  title    : string;
  onClick  : () => void;
  disabled?: boolean;
}> = ({ icon, color, title, onClick, disabled }) => (
  <button
    className = "rs-icon-btn"
    onClick   = {disabled ? undefined : onClick}
    disabled  = {disabled}
    title     = {title}
    style     = {{
      '--rs-btn-bg-hover': `${color}22`,
      display       : 'inline-flex',
      alignItems    : 'center',
      justifyContent: 'center',
      width         : 40,
      height        : 40,
      borderRadius  : 6,
      background    : 'transparent',
      border        : `1px solid transparent`,
      color         : disabled ? '#475569' : color,
      cursor        : disabled ? 'not-allowed' : 'pointer',
      opacity       : disabled ? 0.4 : 0.75,
      transition    : 'opacity 0.15s, background 0.15s',
      flexShrink    : 0,
    } as React.CSSProperties}
  >
    {icon}
  </button>
);

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  overlay: {
    position      : 'fixed',
    inset         : 0,
    zIndex        : 9999,
    background    : 'rgba(0,0,0,0.65)',
    backdropFilter: 'blur(4px)',
    display       : 'flex',
    alignItems    : 'center',
    justifyContent: 'center',
    padding       : 24,
  } as React.CSSProperties,

  modal: {
    background   : 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
    border       : '1px solid #1e293b',
    borderRadius : 14,
    width        : '100%',
    // Élargie le 25/08/2026 : les noms de prise portent le nom de la chaîne, la
    // date à la seconde et, en multi-angle, celui de la caméra. À 860 px, la
    // colonne Titre les coupait presque tous.
    maxWidth     : 1080,
    maxHeight    : '90vh',
    boxShadow    : '0 24px 64px rgba(0,0,0,0.6)',
    overflow     : 'hidden',
    display      : 'flex',
    flexDirection: 'column',
  } as React.CSSProperties,

  header: {
    display      : 'flex',
    alignItems   : 'center',
    gap          : 10,
    padding      : '14px 20px',
    borderBottom : '1px solid #1e293b',
    flexShrink   : 0,
  } as React.CSSProperties,

  headerTitle: {
    fontSize    : 16,
    fontWeight  : 700,
    color       : '#ffffff',
    flex        : 1,
    overflow    : 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace  : 'nowrap',
  } as React.CSSProperties,

  headerIcon: {
    width: 34, height: 34, borderRadius: 10, flexShrink: 0,
    background: 'rgba(96,165,250,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  } as React.CSSProperties,

  headerBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px',
    borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.14)', color: '#e2e8f0',
  } as React.CSSProperties,

  closeIconBtn: {
    width: 32, height: 32, borderRadius: 8, flexShrink: 0, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e8f0',
  } as React.CSSProperties,

  summary: {
    display: 'flex', gap: 10, padding: '14px 20px 6px', flexShrink: 0, flexWrap: 'wrap',
  } as React.CSSProperties,

  emptyIcon: {
    width: 60, height: 60, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.15)',
  } as React.CSSProperties,

  fileIcon: {
    width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(96,165,250,0.12)', color: '#93c5fd',
  } as React.CSSProperties,

  fileIconLive: {
    background: 'rgba(250,204,21,0.14)', color: '#facc15',
  } as React.CSSProperties,

  takeToggle: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    width: 24, height: 24, padding: 0, borderRadius: 6, cursor: 'pointer',
    background: 'rgba(107,71,220,0.18)', border: '1px solid #8f75e8', color: '#c4b5fd',
  } as React.CSSProperties,

  multiviewBadge: {
    display: 'inline-flex', alignItems: 'center', flexShrink: 0,
    padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
    background: '#6b47dc', border: '1px solid #6b47dc', color: '#ffffff',
  } as React.CSSProperties,

  angleBadge: {
    display: 'inline-flex', alignItems: 'center', flexShrink: 0,
    padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
    background: 'transparent', border: '1px solid #8f75e8', color: '#c4b5fd',
  } as React.CSSProperties,

  liveBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
    padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
    background: 'rgba(250,204,21,0.14)', border: '1px solid rgba(250,204,21,0.45)', color: '#fde047',
  } as React.CSSProperties,

  confirmYes: {
    height: 28, padding: '0 12px', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer',
    background: '#dc2626', border: '1px solid #f87171', color: '#ffffff',
  } as React.CSSProperties,

  confirmNo: {
    height: 28, padding: '0 12px', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer',
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.16)', color: '#e2e8f0',
  } as React.CSSProperties,

  kbd: {
    display: 'inline-block', padding: '0 6px', borderRadius: 5, fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
    background: 'linear-gradient(#334155, #1e293b)', border: '1px solid rgba(255,255,255,0.16)', borderBottomWidth: 2, color: '#f8fafc',
  } as React.CSSProperties,

  footer: {
    padding        : '10px 20px',
    borderTop      : '1px solid #0f172a',
    display        : 'flex',
    justifyContent : 'space-between',
    alignItems     : 'center',
    flexShrink     : 0,
    background     : 'rgba(0,0,0,0.2)',
  } as React.CSSProperties,

  iconBtn: {
    background  : 'none',
    border      : 'none',
    cursor      : 'pointer',
    color       : '#ffffff',
    display     : 'flex',
    padding     : 4,
    borderRadius: 6,
    transition  : 'color 0.15s',
  } as React.CSSProperties,

  backBtn: {
    background  : 'rgba(255,255,255,0.06)',
    border      : '1px solid rgba(255,255,255,0.1)',
    cursor      : 'pointer',
    color       : '#ffffff',
    display     : 'flex',
    alignItems  : 'center',
    padding     : '4px 8px',
    borderRadius: 7,
  } as React.CSSProperties,

  table: {
    width          : '100%',
    borderCollapse : 'collapse',
    tableLayout    : 'fixed',
  } as React.CSSProperties,

  th: {
    padding        : '10px 12px',
    textAlign      : 'left' as const,
    fontSize       : 11,
    fontWeight     : 700,
    letterSpacing  : '0.06em',
    textTransform  : 'uppercase' as const,
    cursor         : 'pointer',
    userSelect     : 'none' as const,
    borderBottom   : '1px solid #1e293b',
    // Opaque et collé en haut : les en-têtes restent visibles quand la liste défile.
    background     : '#111a2e',
    position       : 'sticky' as const,
    top            : 0,
    zIndex         : 1,
    transition     : 'color 0.15s',
    whiteSpace     : 'nowrap' as const,
  } as React.CSSProperties,

  tr: {
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    transition  : 'background 0.1s',
  } as React.CSSProperties,

  td: {
    padding      : '8px 12px',
    verticalAlign: 'middle' as const,
    overflow     : 'hidden',
    textOverflow : 'ellipsis',
  } as React.CSSProperties,

  renameInput: {
    width       : '100%',
    fontSize    : 16,
    fontWeight  : 500,
    color       : '#ffffff',
    background  : 'rgba(255,255,255,0.06)',
    border      : '1px solid #60a5fa',
    borderRadius: 5,
    padding     : '3px 7px',
    outline     : 'none',
  } as React.CSSProperties,

  // Bulle « caractère interdit » — AU-DESSUS du champ (sous lui, sauf manque de place),
  // jamais dessus : le curseur de saisie et les caractères déjà tapés doivent rester
  // visibles pendant qu'on continue à écrire. `fixed` + portail direct sur document.body
  // (voir le rendu plus haut) : coordonnées posées au clavier, au-dessus de tout,
  // jamais rognée par l'`overflow: hidden` de la cellule du tableau.
  renameForbiddenBubble: {
    position     : 'fixed',
    zIndex       : 10001,
    background   : '#7f1d1d',
    color        : '#fecaca',
    fontSize     : 12,
    fontWeight   : 600,
    padding      : '4px 9px',
    borderRadius : 6,
    boxShadow    : '0 4px 12px rgba(0,0,0,0.4)',
    whiteSpace   : 'nowrap',
    pointerEvents: 'none',
  } as React.CSSProperties,

  liveDot: {
    display     : 'inline-block',
    width       : 6,
    height      : 6,
    borderRadius: '50%',
    background  : '#facc15',
    flexShrink  : 0,
    boxShadow   : '0 0 5px #facc15',
    animation   : 'rs-pulse 1.4s ease-in-out infinite',
  } as React.CSSProperties,

  errorBanner: {
    margin      : '12px 20px',
    padding     : '9px 14px',
    background  : 'rgba(239,68,68,0.1)',
    border      : '1px solid rgba(239,68,68,0.25)',
    borderRadius: 8,
    display     : 'flex',
    alignItems  : 'center',
    gap         : 8,
  } as React.CSSProperties,

  centered: {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    gap            : 10,
    padding        : 48,
    flex           : 1,
  } as React.CSSProperties,

  closeBtn: {
    background  : 'rgba(255,255,255,0.05)',
    border      : '1px solid rgba(255,255,255,0.1)',
    borderRadius: 7,
    color       : '#ffffff',
    fontSize    : 13,
    fontWeight  : 600,
    padding     : '6px 18px',
    cursor      : 'pointer',
  } as React.CSSProperties,
};

export default RecordingStorageModal;